import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { BaseService } from '../../core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { tap, shareReplay } from 'rxjs/operators';
import { SigninDto } from './dto/signin.dto';
import { Auth } from './dto/auth.dto';
import { BaseUser } from './dto/base-user';
import { JwtStorageService } from './jwt-storage.service';
import { SendMailResponse } from '../../core/interfaces';
import { BuyerCheckEmailDto, BuyerCreateDto, BuyerResetPasswordDto, BuyerUpdateDto } from '../../dto/buyer';
import { Store } from '@ngrx/store';
import * as IndicatorStore from '../../store/indicator';
import { mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { GaService } from '../../services/ga.service';
import { HomeDto } from '../../dto/home';
@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  API_PATH = '/buyer/auth';

  private onLoginSubject = new Subject<any>(); // 이벤트 리스너
  onLoginSubject$ = this.onLoginSubject.asObservable(); // 로그인 여부 리스너

  constructor(
    private jwtStorageService: JwtStorageService,
    private jwtHelper: JwtHelperService,
    private gaService: GaService,
    private store: Store<any>
  ) {
    super();
  }

  setToken(res: Auth) {
    if (!res || !res.token) {
      return;
    }
    this.jwtStorageService.setToken(res.token);
    if (res.refreshToken) {
      this.jwtStorageService.setRefreshToken(res.refreshToken);
    }
  }

  signin(credential: SigninDto): Observable<Auth> {
    this.store.dispatch(new IndicatorStore.ShowIndicator('signin'));
    return this.post<Auth>(`${this.API_PATH}/signin`, credential).pipe(
      tap((res) => {
        this.signout();
        this.setToken(res);
        this.setLoginGA();
        this.store.dispatch(new IndicatorStore.HideIndicator('signin'));
      }),
      shareReplay()
    );
  }

  signinByAdmin(credential: SigninDto): Observable<Auth> {
    credential.password = '1234';
    this.store.dispatch(new IndicatorStore.ShowIndicator('signin_by_admin'));
    return this.post<Auth>(`${this.API_PATH}/signin_by_admin`, credential).pipe(
      tap((res) => {
        this.signout();
        this.setToken(res);
        this.store.dispatch(new IndicatorStore.HideIndicator('signin_by_admin'));
      }),
      shareReplay()
    );
  }

  signup(buyer: BuyerCreateDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('signup'));
    return this.post<Auth>(`${this.API_PATH}/signup`, buyer).pipe(
      tap((res) => {
        this.signout();
        this.setToken(res);
        this.store.dispatch(new IndicatorStore.HideIndicator('signup'));
      }),
      shareReplay()
    );
  }

  signupSimplify(buyer: BuyerCreateDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('signup'));
    return this.post<Auth>(`${this.API_PATH}/signup/simplify`, buyer).pipe(
      tap((res) => {
        this.signout();
        this.setToken(res);
        this.store.dispatch(new IndicatorStore.HideIndicator('signup'));
      }),
      shareReplay()
    );
  }

  emailAuthentication(buyer: BuyerUpdateDto) {
    this.store.dispatch(new IndicatorStore.HideIndicator('emailOtp'));
    return this.post<{ isUsed: boolean }>(`${this.API_PATH}/email-authentication`, buyer).pipe(
      mergeMap((res) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('emailOtp'));
        return of(res);
      })
    );
  }

  getSignupBanner(language: string) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('getSignupBanner'));
    return super
      .get<HomeDto>(`${this.API_PATH}/signupBanner`, { language })
      .pipe(
        mergeMap((homeDto) => {
          this.store.dispatch(new IndicatorStore.HideIndicator('getSignupBanner'));
          return of(homeDto);
        })
      );
  }

  signout(): void {
    this.jwtStorageService.removeToken();
    this.jwtStorageService.removeRefreshToken();
  }

  setLoginGA() {
    const user = this.getUser();
    this.gaService.MOJI_Login(user);
  }

  forgotPassword(request: BuyerCheckEmailDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('forgotPassword'));
    return this.post<SendMailResponse>(`${this.API_PATH}/forgot-password`, request).pipe(
      mergeMap((res) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('forgotPassword'));
        return of(res);
      })
    );
  }

  sendVerificationEmail(email: string) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('emailVerification'));
    return this.post<SendMailResponse>(`${this.API_PATH}/email-verification`, { email }).pipe(
      mergeMap((res) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('emailVerification'));
        return of(res);
      })
    );
  }

  sendVerificationMobile(callingCode: string, mobile: string) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('mobileVerification'));
    return this.post<SendMailResponse>(`${this.API_PATH}/mobile-verification/limiter`, { callingCode, mobile }).pipe(
      mergeMap((res) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('mobileVerification'));
        return of(res);
      })
    );
  }

  sendAuthMobile(id: number, buyer: BuyerUpdateDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('mobileAuthentication'));
    return this.post<Auth>(`${this.API_PATH}/mobile-authentication/limiter/${id}`, buyer).pipe(
      tap((res) => {
        this.signout();
        this.setToken(res);
        this.store.dispatch(new IndicatorStore.HideIndicator('mobileAuthentication'));
      }),
      shareReplay()
    );
  }

  resetPassword(request: BuyerResetPasswordDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('resetPassword'));
    return this.post<SendMailResponse>(`${this.API_PATH}/reset-password`, request).pipe(
      mergeMap((res) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('resetPassword'));
        return of(res);
      })
    );
  }

  // 토큰 유효성 검증
  isAuthenticated(): boolean {
    return this.getUser() ? true : false;
  }

  getId(): number {
    const user = this.getUser();
    return user ? user.id : null;
  }

  getUser(): BaseUser {
    try {
      const token = this.getToken();
      console.log(new BaseUser(this.jwtHelper.decodeToken(token)));
      return token ? new BaseUser(this.jwtHelper.decodeToken(token)) : null;
    } catch (e) {
      return null;
    }
  }

  getToken(): string {
    const token = this.jwtStorageService.getToken();
    if (token) {
      if (this.jwtHelper.isTokenExpired(token)) {
        this.jwtStorageService.removeToken();
        return null;
      }
      return token;
    }
    return null;
  }

  async getRefreshToken(): Promise<BaseUser> {
    const refreshToken = this.jwtStorageService.getRefreshToken();
    if (refreshToken) {
      if (this.jwtHelper.isTokenExpired(refreshToken)) {
        this.jwtStorageService.removeRefreshToken();
        return null;
      }

      await this.post<Auth>(`${this.API_PATH}/token`, { refreshToken })
        .pipe(
          tap((res) => {
            this.setToken(res);
          }),
          shareReplay()
        )
        .toPromise();
      return this.getUser();
    }
    return null;
  }

  emitOnLogin() {
    this.onLoginSubject.next('login');
  }
}
